import React, { useEffect, useState } from "react";
import { ArrowLeft, Eye, Loader2, Mail, Plus, Send, Trash2, Activity } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { AdminHeader, Badge, Field, Panel, Row, Table, borderStyle, inputCls } from "../ui";
import { RichEmailEditor } from "../components/RichEmailEditor";
import { AiFormatBar } from "../components/AiFormatBar";
import { CampaignSchedule } from "../components/CampaignSchedule";
import { CampaignTracking } from "../components/CampaignTracking";

const TONE = { draft: "gold", queued: "gold", scheduled: "gold", sending: "gold", paused: "gold", sent: "green", partial: "gold", failed: "red" };

const htmlToText = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html.replace(/<\/(p|h2|h3|li)>/gi, "</$1>\n\n");
  return div.textContent.replace(/\n{3,}/g, "\n\n").trim();
};

export default function CampaignsAdmin() {
  const [status, setStatus] = useState(null);
  const [list, setList] = useState([]);
  const [segments, setSegments] = useState({ tags: [], sources: [] });
  const [editing, setEditing] = useState(null);
  const [count, setCount] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sendLog, setSendLog] = useState(null);
  const [totals, setTotals] = useState(null);

  const load = () =>
    Promise.all([
      adminApi.get("/admin/marketing/status"),
      adminApi.get("/admin/marketing/campaigns"),
      adminApi.get("/admin/marketing/segments"),
    ]).then(([s, c, g]) => {
      setStatus(s.data);
      setList(c.data.campaigns);
      setSegments(g.data);
    }).catch(() => toast.error("Could not load campaigns"));

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!editing) return;
    adminApi
      .post("/admin/marketing/campaigns/audience-count", { audience: editing.audience })
      .then((r) => setCount(r.data.count))
      .catch(() => setCount(null));
  }, [editing?.audience]);

  // Nothing is stored until the first save, so an abandoned "New mailout" leaves no row behind.
  const create = () => {
    setSendLog(null);
    setEditing({ id: null, subject: "", body: "", body_html: "", template: "standard", send_limit: 0,
      audience: { tags: [], sources: [] }, status: "draft" });
  };

  const open = async (c) => {
    const r = await adminApi.get(`/admin/marketing/campaigns/${c.id}`);
    setSendLog(null);
    setEditing(r.data.campaign);
  };

  /** Saves the draft (creating it the first time) and returns its id. */
  const save = async () => {
    if (["sending", "sent", "queued", "partial"].includes(editing.status) || editing.already_sent) return editing.id;
    setBusy(true);
    try {
      const fields = {
        subject: editing.subject, body: editing.body, body_html: editing.body_html || "",
        cta_label: editing.cta_label, cta_url: editing.cta_url,
        image_url: editing.image_url, template: editing.template || "standard",
        send_limit: editing.send_limit || 0,
        audience: editing.audience,
      };
      let r = editing.id
        ? await adminApi.patch(`/admin/marketing/campaigns/${editing.id}`, fields)
        : await adminApi.post("/admin/marketing/campaigns", fields);
      if (!editing.id) r = await adminApi.get(`/admin/marketing/campaigns/${r.data.campaign.id}`);
      setEditing(r.data.campaign);
      toast.success("Saved");
      load();
      return r.data.campaign.id;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const showPreview = async () => {
    setBusy(true);
    try {
      const id = await save();
      const r = await adminApi.get(`/admin/marketing/campaigns/${id}/preview`);
      setPreview(r.data.html);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build the preview");
    } finally {
      setBusy(false);
    }
  };

  const loadSendLog = async (verify) => {
    setBusy(true);
    try {
      const r = await adminApi.get(`/admin/marketing/campaigns/${editing.id}/send-log`, {
        params: verify ? { verify: true } : {},
      });
      setSendLog(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read the send log");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const id = await save();
      await adminApi.post(`/admin/marketing/campaigns/${id}/test`, { to: testTo });
      toast.success("Test sent", { description: `Check ${testTo}` });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setBusy(false);
    }
  };

  const sendAll = async () => {
    const already = editing.already_sent || 0;
    const limit = editing.send_limit || 0;
    const going = limit ? Math.min(limit, (count || 0) - already) : (count || 0) - already;
    if (!window.confirm(`Send "${editing.subject}" to ${going} people now? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const id = await save();
      const r = await adminApi.post(`/admin/marketing/campaigns/${id}/send`);
      toast.success(`Sending to ${r.data.recipients} people`, {
        description: r.data.per_day
          ? `${r.data.per_day} a day - it carries on by itself each day until everyone has it.`
          : "It works through the list in the background, and picks itself back up if anything interrupts it.",
      });
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally {
      setBusy(false);
    }
  };

  const loadTotals = async () => {
    setBusy(true);
    try {
      const r = await adminApi.get("/admin/marketing/resend-totals", { params: { days: 30 } });
      setTotals(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reach Resend");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.subject || "(untitled)"}"? This cannot be undone.`)) return;
    await adminApi.delete(`/admin/marketing/campaigns/${c.id}`);
    load();
  };

  // Back from a draft nobody typed anything into: don't leave an '(untitled)' row behind.
  const back = async () => {
    const empty = editing.id && editing.status === "draft" && !editing.subject?.trim() && !editing.body?.trim();
    if (empty) await adminApi.delete(`/admin/marketing/campaigns/${editing.id}`).catch(() => {});
    setEditing(null);
    load();
  };

  const toggleTag = (name) => {
    const tags = editing.audience?.tags || [];
    setEditing({
      ...editing,
      audience: { ...editing.audience, tags: tags.includes(name) ? tags.filter((t) => t !== name) : [...tags, name] },
    });
  };

  // ------------------------------------------------------------ composer
  if (editing) {
    const locked = ["sending", "sent", "queued", "partial"].includes(editing.status) || (editing.already_sent || 0) > 0;
    const sendable = !["sending", "sent", "queued"].includes(editing.status);
    return (
      <div className="space-y-6" data-testid="campaign-composer">
        <AdminHeader title={editing.subject || "New mailout"} subtitle={`Status: ${editing.status}`}>
          <button onClick={back} className="btn-ghost inline-flex items-center gap-2" data-testid="campaign-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        </AdminHeader>

        <div className="grid gap-6 lg:grid-cols-[1.4fr,1fr]">
          <Panel>
            <Field label="Subject line" hint="What lands in their inbox - keep it plain and specific">
              <input
                value={editing.subject || ""}
                onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                disabled={locked}
                className={inputCls}
                data-testid="campaign-subject"
              />
            </Field>
            <div className="mt-4">
              <Field
                as="div"
                label="Message"
                hint="Type it like an email. A link on its own line becomes a button in the email."
              >
                <RichEmailEditor
                  value={editing.body_html || ""}
                  onChange={(html, text) => setEditing((cur) => ({ ...cur, body_html: html, body: text }))}
                  disabled={locked}
                />
              </Field>
              <AiFormatBar
                html={editing.body_html || ""}
                disabled={locked}
                onApply={(html) => setEditing((cur) => ({ ...cur, body_html: html, body: htmlToText(html) }))}
              />
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Field label="Button text" hint="Optional">
                <input value={editing.cta_label || ""} onChange={(e) => setEditing({ ...editing, cta_label: e.target.value })} disabled={locked} className={inputCls} data-testid="campaign-cta-label" />
              </Field>
              <Field label="Button link">
                <input value={editing.cta_url || ""} onChange={(e) => setEditing({ ...editing, cta_url: e.target.value })} disabled={locked} className={inputCls} placeholder="https://nqsprings.com.au/shop" />
              </Field>
            </div>
            <div className="mt-4">
              <Field label="Design" hint="The RACEX design is a dark race-day email: price, discount and one button above the fold.">
                <select
                  value={editing.template || "standard"}
                  onChange={(e) => setEditing({ ...editing, template: e.target.value })}
                  disabled={locked}
                  className={inputCls}
                  data-testid="campaign-template"
                >
                  <option value="standard">Standard newsletter (light)</option>
                  <option value="racex">RACEX offer (dark, race day)</option>
                </select>
              </Field>
            </div>
            <div className="mt-4">
              <Field label="Flyer or photo" hint="Full web address of the image. It sits at the top of the email and links to the button link.">
                <input value={editing.image_url || ""} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} disabled={locked} className={inputCls} data-testid="campaign-image-url" />
              </Field>
              {editing.image_url && (
                <img src={editing.image_url} alt="" className="mt-3 max-h-64 rounded-lg border" style={borderStyle} data-testid="campaign-image-preview" />
              )}
            </div>
          </Panel>

          <div className="space-y-6">
            <Panel>
              <p className="text-xs font-semibold uppercase tracking-wider gold-text">Who gets it</p>
              <p className="mt-2 text-xs text-[color:var(--nq-stone)]">
                No tags picked means everyone who is still subscribed.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {segments.tags.length === 0 && (
                  <p className="text-xs text-[color:var(--nq-stone)]">No tags yet - add some on the Contacts screen.</p>
                )}
                {segments.tags.map((t) => {
                  const on = (editing.audience?.tags || []).includes(t.name);
                  return (
                    <button
                      key={t.name}
                      onClick={() => !locked && toggleTag(t.name)}
                      className={`rounded-full border px-3 py-1.5 text-xs transition ${on ? "gold-text border-[color:var(--nq-gold)]" : "text-[color:var(--nq-stone)]"}`}
                      style={on ? undefined : borderStyle}
                      data-testid={`campaign-tag-${t.name}`}
                    >
                      {t.name} ({t.count})
                    </button>
                  );
                })}
              </div>
              <p className="mt-4 text-sm" data-testid="campaign-audience-count">
                Going to <strong>{count ?? "-"}</strong> people
                {editing.already_sent ? ` · ${editing.already_sent} already done` : ""}
              </p>
              <div className="mt-4">
                <Field
                  label="Send at most, per day"
                  hint="Leave 0 to send the lot. On a big list, 2000 a day keeps you out of the spam folder - it carries on by itself the next day until everyone has it."
                >
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={editing.send_limit ?? 0}
                    onChange={(e) => setEditing({ ...editing, send_limit: Number(e.target.value) })}
                    className={`${inputCls} max-w-[8rem]`}
                    data-testid="campaign-send-limit"
                  />
                </Field>
              </div>
            </Panel>

            <Panel>
              <p className="text-xs font-semibold uppercase tracking-wider gold-text">Send</p>
              <div className="mt-3 space-y-3">
                <Field label="Send a test to">
                  <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@nqsprings.com.au" className={inputCls} data-testid="campaign-test-email" />
                </Field>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => save().catch(() => {})} disabled={busy || locked} className="btn-outline-nq px-4 py-2 text-xs disabled:opacity-50" data-testid="campaign-save">
                    Save draft
                  </button>
                  <button onClick={showPreview} disabled={busy} className="btn-outline-nq inline-flex items-center gap-2 px-4 py-2 text-xs disabled:opacity-50" data-testid="campaign-preview">
                    <Eye className="h-3.5 w-3.5" /> Preview
                  </button>
                  <button onClick={sendTest} disabled={busy || !testTo} className="btn-outline-nq inline-flex items-center gap-2 px-4 py-2 text-xs disabled:opacity-50" data-testid="campaign-send-test">
                    <Mail className="h-3.5 w-3.5" /> Send test
                  </button>
                  <button onClick={sendAll} disabled={busy || !sendable || !count || editing.status === "scheduled"} className="btn-gold inline-flex items-center gap-2 px-4 py-2 text-xs disabled:opacity-50" data-testid="campaign-send-all">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    Send to {count ?? 0}
                  </button>
                </div>
                {sendable && (
                  <CampaignSchedule campaign={editing} beforeSchedule={save} onChanged={(id) => open({ id })} disabled={busy || !count} />
                )}
                {editing.status === "paused" && editing.resume_at && (
                  <p className="text-[11px] gold-text" data-testid="campaign-resume-at">
                    Paused - carries on by itself around {new Date(editing.resume_at).toLocaleString()}.
                  </p>
                )}
                {editing.last_error && (
                  <p className="text-[11px] text-red-300" data-testid="campaign-last-error">{editing.last_error}</p>
                )}
                <p className="text-[11px] leading-relaxed text-[color:var(--nq-stone)]">
                  Every email carries your business name, address, phone and a working unsubscribe
                  link - that is the law here, so it is added for you and cannot be removed.
                </p>
              </div>
            </Panel>

            {(editing.already_sent || 0) > 0 && (
              <CampaignTracking tracking={editing.tracking} trackingConfigured={status?.tracking_configured} sent={editing.already_sent} />
            )}

            <Panel>
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold uppercase tracking-wider gold-text">Send log</p>
                <div className="flex gap-2">
                  <button onClick={() => loadSendLog(false)} disabled={busy} className="btn-outline-nq inline-flex items-center gap-2 px-3 py-1.5 text-[11px] disabled:opacity-50" data-testid="campaign-send-log">
                    <Activity className="h-3.5 w-3.5" /> Check
                  </button>
                  <button onClick={() => loadSendLog(true)} disabled={busy} className="btn-outline-nq px-3 py-1.5 text-[11px] disabled:opacity-50" data-testid="campaign-send-log-verify">
                    Ask Resend
                  </button>
                </div>
              </div>
              {!sendLog ? (
                <p className="mt-3 text-xs text-[color:var(--nq-stone)]">
                  Press Check to see every batch this mailout handed to Resend, and how many it accepted.
                </p>
              ) : (
                <div className="mt-3 space-y-2 text-xs" data-testid="campaign-send-log-result">
                  <p>
                    Handed to Resend <strong>{sendLog.asked_total}</strong> · accepted{" "}
                    <strong>{sendLog.accepted_total}</strong> · recorded sent{" "}
                    <strong>{sendLog.stats?.sent || 0}</strong>
                    {sendLog.stats?.failed ? ` · ${sendLog.stats.failed} failed` : ""}
                  </p>
                  {sendLog.last_error && (
                    <p className="text-red-300" data-testid="campaign-send-log-error">{sendLog.last_error}</p>
                  )}
                  <div className="max-h-60 space-y-1 overflow-y-auto">
                    {(sendLog.batches || []).map((b, i) => (
                      <div key={i} className="flex flex-wrap gap-x-3 text-[11px] text-[color:var(--nq-stone)]">
                        <span>{new Date(b.at).toLocaleString()}</span>
                        <span>{b.accepted}/{b.asked} accepted</span>
                        {b.error && <span className="text-red-300">{b.error}</span>}
                        {b.delivery && (
                          <span className={b.delivery.error ? "text-red-300" : "text-[color:var(--nq-gold)]"}>
                            {b.delivery.error || `Resend says: ${b.delivery.last_event || "unknown"}`}
                          </span>
                        )}
                      </div>
                    ))}
                    {(sendLog.batches || []).length === 0 && (
                      <p className="text-[11px] text-[color:var(--nq-stone)]">
                        No batch records yet - this mailout was sent before batch logging existed, or has not been sent.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </Panel>
          </div>
        </div>

        {preview && (
          <div className="fixed inset-0 z-50 flex flex-col bg-black/80 p-4 sm:p-8" data-testid="campaign-preview-modal">
            <div className="mx-auto flex w-full max-w-3xl items-center justify-between pb-3">
              <p className="text-sm font-semibold text-white">Email preview</p>
              <button onClick={() => setPreview(null)} className="btn-outline-nq px-3 py-1.5 text-xs" data-testid="campaign-preview-close">
                Close
              </button>
            </div>
            <iframe
              title="Email preview"
              srcDoc={preview}
              className="mx-auto h-full w-full max-w-3xl rounded-lg border bg-white"
              style={borderStyle}
              data-testid="campaign-preview-frame"
            />
          </div>
        )}
      </div>
    );
  }

  // ------------------------------------------------------------ list
  return (
    <div className="space-y-6" data-testid="campaigns-admin">
      <AdminHeader title="Mailouts" subtitle="News, workshop updates and surplus stock alerts">
        <button onClick={create} className="btn-gold inline-flex items-center gap-2" data-testid="campaign-new">
          <Plus className="h-4 w-4" /> New mailout
        </button>
      </AdminHeader>

      {status && (
        <Panel>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {status.resend_configured ? (
              <Badge tone="green" data-testid="resend-status">Resend connected</Badge>
            ) : (
              <Badge tone="red" data-testid="resend-status">Resend not set up</Badge>
            )}
            <span className="text-xs text-[color:var(--nq-stone)]">
              From {status.from_address} · {status.subscribed} subscribed · {status.unsubscribed} opted out
              {status.tracking_configured ? " · open tracking on" : " · open tracking not connected yet"}
            </span>
            <button onClick={loadTotals} disabled={busy} className="btn-outline-nq ml-auto px-3 py-1.5 text-[11px] disabled:opacity-50" data-testid="resend-totals">
              Check Resend's own count
            </button>
          </div>
          {totals && (
            <div className="mt-3 text-xs" data-testid="resend-totals-result">
              {totals.available ? (
                <p>
                  Resend, {totals.from} to {totals.to}:{" "}
                  {Object.entries(totals.totals || {}).map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`).join(" · ") || "no figures returned"}
                </p>
              ) : (
                <p className="text-red-300">
                  {totals.error}
                  {totals.needs_audit_key && " — add a full-access Resend key as RESEND_AUDIT_API_KEY to read these figures."}
                </p>
              )}
            </div>
          )}
        </Panel>
      )}

      {list.length === 0 ? (
        <Panel><p className="text-sm text-[color:var(--nq-stone)]">No mailouts yet.</p></Panel>
      ) : (
        <Table head={["Subject", "Audience", "Status", "Sent", "Opened", ""]}>
          {list.map((c) => (
            <Row key={c.id} testid="campaign-row">
              <td className="p-3">
                <button onClick={() => open(c)} className="text-left font-medium hover:text-[color:var(--nq-gold)]">
                  {c.subject || "(untitled)"}
                </button>
              </td>
              <td className="p-3 text-xs text-[color:var(--nq-stone)]">
                {(c.audience?.tags || []).join(", ") || "everyone"}
              </td>
              <td className="p-3">
                <Badge tone={TONE[c.status] || "gold"}>{c.status}</Badge>
                {c.status === "scheduled" && c.scheduled_at && (
                  <span className="mt-1 block text-[11px] text-[color:var(--nq-stone)]">{new Date(c.scheduled_at).toLocaleString()}</span>
                )}
              </td>
              <td className="p-3 text-xs">
                {c.stats?.sent || 0}
                {c.stats?.failed ? ` (${c.stats.failed} failed)` : ""}
                {c.last_error && (
                  <span className="mt-1 block text-[11px] text-red-300" data-testid={`campaign-error-${c.id}`}>
                    {c.last_error}
                  </span>
                )}
              </td>
              <td className="p-3 text-xs" data-testid={`campaign-opens-${c.id}`}>
                {c.tracking ? `${c.tracking.opened} opened · ${c.tracking.clicked} clicked` : "-"}
              </td>
              <td className="p-3 text-right">
                {!["sending", "queued"].includes(c.status) && (
                  <button onClick={() => remove(c)} className="text-[color:var(--nq-stone)] hover:text-red-300" data-testid={`campaign-delete-${c.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </td>
            </Row>
          ))}
        </Table>
      )}
    </div>
  );
}
